import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import userService from '../services/userService';
import otpService from '../services/otpService';

const useUserStore = create(
  persist(
    (set, get) => ({
      // State
      token: null,
      loginFlag: false,
      products: [],
      product: {},
      cart: [],
      cartCount: 0,
      orders: [],
      address: [],
      cartUpdate: [],
      userProfile: {
        id: null,
        email: "",
        phone: "",
        name: "",
        first_name: "",
        last_name: "",
        date_of_birth: "",
        is_active: false,
        date_joined: ""
      },
      // OTP related state
      otpSent: false,
      otpPhone: "",
      otpExpiresAt: null,

      // Actions
      setLoginFlag: (value) => set({ loginFlag: value }),
      setToken: (value) => set({ token: value }),
      setCart: (value) => set({ cart: value }),
      setAddress: (value) => set({ address: value }),
      setOrders: (value) => set({ orders: value }),
      setProducts: (value) => set({ products: value }),
      setProduct: (value) => set({ product: value }),
      setCartCount: (value) => {
        if (value !== undefined) {
          set({ cartCount: value });
        } else {
          // If no value provided, fetch from API
          get().fetchCartCount();
        }
      },
      setCartUpdate: (value) => set({ cartUpdate: value }),
      setUserProfile: (value) => set({ userProfile: value }),
      
      // OTP related actions
      setOTPSent: (value) => set({ otpSent: value }),
      setOTPPhone: (value) => set({ otpPhone: value }),
      setOTPExpiresAt: (value) => set({ otpExpiresAt: value }),

      // Async actions
      fetchCartCount: async () => {
        try {
          const result = await userService.cartCount();
          set({ cartCount: result.data.count });
        } catch (error) {
          console.error('Error fetching cart count:', error);
        }
      },

      // Cart operations
      plusMinus: async (payload) => {
        const { productId, action, source, index, productType = 'product' } = payload;
        const state = get();
        
        try {
          let apiResult;
          
          if (action === "inc") {
            // Call appropriate addCart API based on product type
            if (productType === 'otc') {
              apiResult = await userService.addOTCCart(productId);
            } else {
              apiResult = await userService.addCart(productId);
            }
          } else {
            // Call appropriate removeCart API based on product type
            if (productType === 'otc') {
              apiResult = await userService.removeOCart(productId);
            } else {
              apiResult = await userService.removePCart(productId);
            }
          }
          
          if (apiResult && apiResult.status === 200) {
            // Update local state
            let productsList = [...state.products];
            let cartList = Array.isArray(state.cart) ? [...state.cart] : [];
            let productview = { ...state.product };
            let tempArray = [...state.cartUpdate];

            if (action === "inc") {
              if (source === "product") {
                // For OTC products, use quantity_in_cart, for regular products use cart
                if (productType === 'otc') {
                  productsList[index].quantity_in_cart = (productsList[index].quantity_in_cart || 0) + 1;
                } else {
                  productsList[index].cart = (productsList[index].cart || 0) + 1;
                }
              }
              if (source === "cart") {
                cartList[index].quantity = cartList[index].quantity + 1;
              }
              if (source === "productview") {
                if (productType === 'otc') {
                  productview.quantity_in_cart = (productview.quantity_in_cart || 0) + 1;
                } else {
                  productview.cart = (productview.cart || 0) + 1;
                }
              }
              
              tempArray.push({ productId, quantity: 1 });
            } else {
              if (source === "product") {
                if (productType === 'otc') {
                  productsList[index].quantity_in_cart = Math.max(0, (productsList[index].quantity_in_cart || 0) - 1);
                } else {
                  productsList[index].cart = Math.max(0, (productsList[index].cart || 0) - 1);
                }
              }
              if (source === "cart") {
                cartList[index].quantity = Math.max(0, cartList[index].quantity - 1);
              }
              if (source === "productview") {
                if (productType === 'otc') {
                  productview.quantity_in_cart = Math.max(0, (productview.quantity_in_cart || 0) - 1);
                } else {
                  productview.cart = Math.max(0, (productview.cart || 0) - 1);
                }
              }
              
              tempArray.push({ productId, quantity: -1 });
            }

            // Update cart body
            const quantitiesById = tempArray.reduce((acc, item) => {
              const { productId, quantity } = item;
              acc[productId] = (acc[productId] || 0) + quantity;
              return acc;
            }, {});

            const result = Object.entries(quantitiesById).map(
              ([productId, quantity]) => ({
                productId: parseInt(productId),
                quantity,
              })
            );

            set({
              products: productsList,
              cart: cartList,
              product: productview,
              cartUpdate: result
            });
            
            // Fetch the real cart count from API
            await get().fetchCartCount();
          }
        } catch (error) {
          console.error('Error in plusMinus operation:', error);
          // Revert local changes on error
          await get().fetchCartCount();
        }
      },

      // Address operations
      fetchAllAddress: async () => {
        try {
          const result = await userService.getAllAddress();
          set({ address: result.data });
        } catch (error) {
          console.error('Error fetching addresses:', error);
        }
      },

      addAddress: async (payload) => {
        try {
          const result = await userService.addAddress(payload);
          // Refresh addresses after adding
          get().fetchAllAddress();
          return result;
        } catch (error) {
          console.error('Error adding address:', error);
          throw error;
        }
      },

      updateAddress: async (id, payload) => {
        try {
          const result = await userService.updateAddress(id, payload);
          // Refresh addresses after updating
          get().fetchAllAddress();
          return result;
        } catch (error) {
          console.error('Error updating address:', error);
          throw error;
        }
      },

      deleteAddress: async (id) => {
        try {
          const result = await userService.deleteAddress(id);
          // Refresh addresses after deleting
          get().fetchAllAddress();
          return result;
        } catch (error) {
          console.error('Error deleting address:', error);
          throw error;
        }
      },

      // Product operations
      fetchProducts: async (params) => {
        try {
          const result = await userService.getProducts(params);
          set({ products: result.data.results || [] });
          return result.data;
        } catch (error) {
          console.error('Error fetching products:', error);
          throw error;
        }
      },

      fetchProductById: async (id) => {
        try {
          const result = await userService.getProductByID(id);
          set({ product: result.data });
          return result.data;
        } catch (error) {
          console.error('Error fetching product:', error);
          throw error;
        }
      },

      fetchOtcById: async (id) => {
        try {
          const result = await userService.getOtcByID(id);
          set({ product: result.data });
          return result.data;
        } catch (error) {
          console.error('Error fetching OTC product:', error);
          throw error;
        }
      },

      // Cart operations
      addToCart: async (id) => {
        try {
          const result = await userService.addCart(id);
          // Refresh cart count after adding
          await get().fetchCartCount();
          return result;
        } catch (error) {
          console.error('Error adding to cart:', error);
          throw error;
        }
      },

      addOtcToCart: async (id) => {
        try {
          const result = await userService.addOTCCart(id);
          // Refresh cart count after adding
          await get().fetchCartCount();
          return result;
        } catch (error) {
          console.error('Error adding OTC to cart:', error);
          throw error;
        }
      },

      // Cart deletion methods
      deletePCart: async (id) => {
        try {
          const result = await userService.deletePCart(id);
          // Refresh cart count after deleting
          await get().fetchCartCount();
          return result;
        } catch (error) {
          console.error('Error deleting product from cart:', error);
          throw error;
        }
      },

      deleteOCart: async (id) => {
        try {
          const result = await userService.deleteOCart(id);
          // Refresh cart count after deleting
          await get().fetchCartCount();
          return result;
        } catch (error) {
          console.error('Error deleting OTC from cart:', error);
          throw error;
        }
      },

      fetchCart: async () => {
        try {
          const result = await userService.getCart();
          // Store cart items array, not the entire response
          const cartItems = result.data.cart?.cart_items || [];
          set({ cart: cartItems });
          return result.data;
        } catch (error) {
          console.error('Error fetching cart:', error);
          throw error;
        }
      },

      // Orders operations
      fetchOrders: async () => {
        try {
          const result = await userService.getOrders();
          set({ orders: result.data });
          return result.data;
        } catch (error) {
          console.error('Error fetching orders:', error);
          throw error;
        }
      },

      fetchOrderById: async (id) => {
        try {
          const result = await userService.getOrderbyId(id);
          return result.data;
        } catch (error) {
          console.error('Error fetching order:', error);
          throw error;
        }
      },

      // Checkout
      checkout: async () => {
        try {
          const result = await userService.checkOut();
          return result.data;
        } catch (error) {
          console.error('Error during checkout:', error);
          throw error;
        }
      },

      // Search
      searchProducts: async (searchTerm) => {
        try {
          const result = await userService.searchBar(searchTerm);
          return result.data;
        } catch (error) {
          console.error('Error searching products:', error);
          throw error;
        }
      },

      // OTC operations
      fetchOtcProducts: async (params) => {
        try {
          const result = await userService.getOtcProducts(params);
          return result.data;
        } catch (error) {
          console.error('Error fetching OTC products:', error);
          throw error;
        }
      },

      fetchCategories: async () => {
        try {
          const result = await userService.getCategories();
          return result.data;
        } catch (error) {
          console.error('Error fetching categories:', error);
          throw error;
        }
      },

      fetchManufacturers: async () => {
        try {
          const result = await userService.getManufacturer();
          return result.data;
        } catch (error) {
          console.error('Error fetching manufacturers:', error);
          throw error;
        }
      },

      // User Profile operations
      fetchUserProfile: async () => {
        try {
          const result = await userService.getUserProfile();
          set({ userProfile: result.data });
          return result.data;
        } catch (error) {
          console.error('Error fetching user profile:', error);
          throw error;
        }
      },

      updateUserProfile: async (userId, payload) => {
        try {
          const result = await userService.updateUserProfile(userId, payload);
          set({ userProfile: result.data });
          return result.data;
        } catch (error) {
          console.error('Error updating user profile:', error);
          throw error;
        }
      },

      patchUserProfile: async (userId, payload) => {
        try {
          const result = await userService.patchUserProfile(userId, payload);
          set({ userProfile: result.data });
          return result.data;
        } catch (error) {
          console.error('Error patching user profile:', error);
          throw error;
        }
      },

      // OTP Authentication methods
      sendOTP: async (phone) => {
        try {
          // Validate phone number
          const validation = otpService.validatePhoneNumber(phone);
          if (!validation.isValid) {
            throw new Error(validation.errors.join(', '));
          }

          const result = await otpService.sendOTP(phone);
          
          // Store OTP state
          const expiresAt = new Date(Date.now() + 5 * 60 * 1000); // 5 minutes from now
          set({ 
            otpSent: true, 
            otpPhone: phone, 
            otpExpiresAt: expiresAt 
          });
          
          return result;
        } catch (error) {
          console.error('Error sending OTP:', error);
          throw error;
        }
      },

      verifyOTP: async (phone, otpCode) => {
        try {
          // Validate OTP
          const validation = otpService.validateOTP(otpCode);
          if (!validation.isValid) {
            throw new Error(validation.errors.join(', '));
          }

          const result = await otpService.verifyOTP(phone, otpCode);
          
          // Update authentication state
          set({ 
            token: result.token,
            loginFlag: true,
            userProfile: result.user,
            otpSent: false,
            otpPhone: "",
            otpExpiresAt: null
          });
          
          return result;
        } catch (error) {
          console.error('Error verifying OTP:', error);
          throw error;
        }
      },

      logout: () => {
        set({ 
          token: null,
          loginFlag: false,
          userProfile: {
            id: null,
            email: "",
            phone: "",
            name: "",
            first_name: "",
            last_name: "",
            date_of_birth: "",
            is_active: false,
            date_joined: ""
          },
          otpSent: false,
          otpPhone: "",
          otpExpiresAt: null,
          cart: [],
          cartCount: 0,
          orders: [],
          address: []
        });
        
        // Clear localStorage
        localStorage.removeItem('pharmaman-user-store');
      },

      isOTPExpired: () => {
        const state = get();
        if (!state.otpExpiresAt) return true;
        return new Date() > new Date(state.otpExpiresAt);
      },

      clearOTPState: () => {
        set({ 
          otpSent: false, 
          otpPhone: "", 
          otpExpiresAt: null 
        });
      },
    }),
    {
      name: 'pharmaman-user-store',
      partialize: (state) => ({
        token: state.token,
        loginFlag: state.loginFlag,
        cart: state.cart,
        cartCount: state.cartCount,
      }),
    }
  )
);

export default useUserStore;
